import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import style from './extensionRequestForm.module.scss';
import { Button } from 'components/UI/button/button';
import DropDownSelect from 'components/UI/dropDownSelect/dropDownSelect';
import DatePicker from 'components/UI/DatePicker/datePicker';
import FileUpload, { DocumentAdditionParams, DocumentDeletionParams } from 'components/UI/FileUpload/fileUpload';
import { Input } from "components/UI/input/input";
import { DropdownOptions, useGetCountries } from 'actions/locationsActions';
import { fileToBase64, parseDateString, isEmptyDropDownValue, getFieldDocumentNames, parseFileExtensions } from 'utils/commonFunctions';
import { DocumentName, DocumentNames } from 'actions/documentActions';
import { validEmailRegex, validPhoneNumberRegex, validZipCodePostalCodeRegex } from 'utils/regexValidations';
import { AllowedExtensions } from 'utils/imageFunctions';
import { MultilineInput } from 'components/UI/multilineInput/multilineInput';
import { useParams } from 'react-router-dom';
import { ContactInfo, useGetContactInfo } from 'actions/contactActions';
import { useLocation } from 'react-router-dom';
import { useGetUser } from 'actions/userActions';

interface ExtensionFormProps {
  onSubmitForm: (values: ExtensionFormFields) => void
  preloadedFiles?: Partial<Record<keyof FileUploads, File[]>>;
}

export interface ExtensionFormFields {
  textRequestDetails: string;
  department?: string;
  applicationType?: string;
  comments?: string;
  fileUploads?: base64UploadsExtension;
  caseAnalyst?: string;
  assignedLawyer?: string;
  matterType?: string;
}

const departmentOptions = [
  { value: 'hr', label: 'Human Resources' },
  { value: 'it', label: 'Information Technology' },
  { value: 'finance', label: 'Finance' },
];

const applicationTypeOptions = [
  { value: 'full-time', label: 'Full-Time' },
  { value: 'part-time', label: 'Part-Time' },
  { value: 'intern', label: 'Internship' },
];

const titleOptions = [
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'Dr.', label: 'Dr.' }
];

export interface FileUploads {
  passportUpload?: File[];
  resumeUpload?: File[];
  canadianUpload?: File[];
  jobDescUpload?: File[];
}

export type base64UploadsExtension = {
  passportUpload?: string[];
  resumeUpload?: string[];
  canadianUpload?: string[];
  jobDescUpload?: File[];
};

export const ExtensionRequest = ({ onSubmitForm, preloadedFiles}: ExtensionFormProps) => {
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, setValue, getValues, trigger } = useForm<ExtensionFormFields>(); 
  const [uploads, setUploads] = useState<FileUploads>({});
  const [documentNames, setDocumentNames] = useState<DocumentNames>({ localDocuments: [], uploadedDocuments: [] } as DocumentNames);
  const [provinceOptions, setProvinceOptions] = useState<DropdownOptions[]>([]);
  const { contID } = useParams<{ contID: string }>();
  const [sameContID, setSameContID] = useState<string>("");
  const clearUploadsAndDocuments = () => {
    setUploads({});
    setDocumentNames({ localDocuments: [], uploadedDocuments: [] });
  };

  const {
    data: userInfo,
  } = useGetUser();
  
  useEffect(() => {
    clearUploadsAndDocuments();
    
    if (preloadedFiles) {
      Object.entries(preloadedFiles).forEach(([fieldId, files]) => {
        if (files && files.length > 0) {
          addDocuments({ fieldId, files });
        }
      });
    }
  }, [preloadedFiles]);

  const {
    data: contactInfo,
    error,
    isLoading,
  } = useGetContactInfo(
    userInfo?.id && contID
      ? {
        contID: parseInt(contID),
        usrID: parseInt(userInfo.id),
      }
      : { contID: 0, usrID: 0 }
  );
  
  const [contactTitle, setContactTitle] = useState<string | null>(null);
  const [contactFirstName, setContactFirstName] = useState<string | null>(null);
  const [contactLastName, setContactLastName] = useState<string | null>(null);
  const [contactBirthday, setContactBirthday] = useState<string | null>(null);

  const setContactFields = (contact: ContactInfo) => {
    setContactTitle(contact.contTitle ?? null);
    setContactFirstName(contact.contChristianNames ?? null);
    setContactLastName(contact.contSurname ?? null);
    setContactBirthday(contact.contDob ? contact.contDob.toString() : null);
  };

  if ((contID !== sameContID) && contactInfo) {
    setContactFields(contactInfo);
    setSameContID(contID);
  }

  const {
    data: countryOptions,
    error: errorCountryOptions,
    isLoading: countryOptionsIsLoading,
  } = useGetCountries();


  const onSubmit = async (values: ExtensionFormFields) => {
    // Perform additional validation to catch autocomplete bypass issues
    try {
      
      const uploadsBase64Promises = Object.entries(uploads).flatMap(([uploadType, files]) => {
        return files?.map((file: File) => 
          fileToBase64(file).then(base64 => {
            const extension = file.name.split('.').pop();  
            const base64RemoveExtension = base64.split(',')[1];
            return { 
              uploadType, 
              base64: base64RemoveExtension, 
              docExt: extension, 
              docName: file.name 
            };
          })
        ) || [];
      });
    
      const base64Results = await Promise.all(uploadsBase64Promises);
    
      // Group the Base64 objects by their uploadType
      const groupedBase64 = base64Results.reduce((acc, { uploadType, base64, docExt, docName }) => {
        if (!acc[uploadType]) {
          acc[uploadType] = [];
        }
        acc[uploadType].push({ base64, docExt, docName });
        return acc;
      }, {} as Record<string, Array<{ base64: string, docExt: string, docName: string }>>);
    
      onSubmitForm({ ...values, fileUploads: groupedBase64 });
    } catch (e) {
      console.error(e);
    }
  };
  
  const handleFormValueChange = (name: string, value: string) => {
    setValue(name as keyof ExtensionFormFields, value);
    trigger(name as keyof ExtensionFormFields);
  };


  const handleCalendarChange = (name: string, value: string) => {
    if (value) {
      handleFormValueChange(name as keyof ExtensionFormFields, value);
    }
  };

  const addDocuments = ({ files, fieldId }: DocumentAdditionParams) => {
    clearErrors(fieldId as keyof ExtensionFormFields);

    const { supportedFiles, error } = parseFileExtensions(files, AllowedExtensions);
    if (error) {
      setError(fieldId as keyof ExtensionFormFields, error);
    }
    if (supportedFiles.length === 0) return;
    const supportedDocNameSet = new Set(supportedFiles.map(file => file.name));

    setUploads(prevUploads => {
      const key = fieldId as keyof FileUploads;
      const filteredUploads = Object.entries(prevUploads).reduce<FileUploads>((acc, [key, existingFiles]: [string, File[]]) => {
        return {
          ...acc,
          [key]: existingFiles.filter(file => !supportedDocNameSet.has(file.name)),
        };
      }, {} as FileUploads);
      
      const currentFiles = filteredUploads[key] || [];

      const updatedUploads = { 
        ...filteredUploads, 
        [key]: [...currentFiles, ...supportedFiles]
      };
      return updatedUploads;
    });

    setDocumentNames(prevDocumentNames => {
      const newDocuments = supportedFiles.map(file => {
        return {
          name: file.name,
          fieldId: fieldId,
          permanent: false,
        } as DocumentName;
      });
      const filteredDocumentNames = {
        ...prevDocumentNames,
        localDocuments: prevDocumentNames.localDocuments.filter(doc => !supportedDocNameSet.has(doc.name)),
      };
      const updatedDocumentNames = {
        ...filteredDocumentNames,
        localDocuments: [...filteredDocumentNames.localDocuments, ...newDocuments],
      };
      
      return updatedDocumentNames;
    });
  };

  const deleteDocument = ({ name, fieldId }: DocumentDeletionParams) => {
    clearErrors(fieldId as keyof ExtensionFormFields);

    setUploads(prevUploads => {
      const key = fieldId as keyof FileUploads;
      return {
        ...prevUploads,
        [key]: prevUploads[key]?.filter(file => file.name !== name),
      };
    });
    setDocumentNames(prevDocumentNames => {
      return {
        ...prevDocumentNames,
        localDocuments: prevDocumentNames.localDocuments.filter(doc => doc.name !== name || doc.fieldId !== fieldId),
      } as DocumentNames;
    });
  };

  const requestDetailsValidationSchema = {
    required: "Details about the request are required",
    maxLength: {
      value: 1000,
      message: "Character Limit: 1,000 characters"
    }
  };

  const phoneValidationSchema = { 
    required: "Telephone is required",
    minLength: {
      value: 10,
      message: "Telephone must be at least 10 characters long",
    },
    pattern: {
      value: validPhoneNumberRegex,
      message: "Check Telephone format",
    },
  };

  const emailValidationSchema = {
    required: "Email is required",
    pattern: {
      value: validEmailRegex,
      message: "Entered value does not match email format",
    }
  };

  const postalZipCodeValidationSchema = { 
    pattern: {
      value: validZipCodePostalCodeRegex,
      message: "invalid format",
    },
  };

  const commonProvinceProps = {
    id: "province",
    validationSchema: { required: "State/Province is required" },
    labelClass: style.labels,
    placeholder: "State/Province",
    register: register,
    errors: errors,
  };

  return (
    <div className={style.formContent}>
      <form onSubmit={handleSubmit(onSubmit)} className={style.applicationForm}>

        <div className={style.formSection}>
          <div className={style.title}>
            <h4><span>1</span> Applicant Details</h4>
          </div>

          <div className={style.row}>
            <div className={style.rowColumnOne}>
              <Input
                id='title'
                placeholder='Title'
                register={register}
                errors={errors}
                label="Title"
                setValue={setValue}
                defaultValue={contactTitle!}
                inputClass={style.readOnly} 
                labelClass={style.labels} 
                validationSchema={{ required: "Title is required" }}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <Input
                id="firstName"
                label="Given Name(s) - as stated on passport"
                register={register} 
                setValue={setValue}
                defaultValue={contactFirstName!}
                placeholder="Given Name(s)" 
                inputClass={style.readOnly} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={{ required: "Given Name(s) is required" }}
              />
            </div>
            <div className={style.rowColumnTwo}>
              <Input
                id="lastName"
                label="Last Name - as stated on passport"
                register={register} 
                setValue={setValue}
                defaultValue={contactLastName!}
                placeholder="Last Name" 
                inputClass={style.readOnly} 
                labelClass={style.labels} 
                errors={errors} 
                validationSchema={{ required: "Last Name is required" }}
              />
            </div>
            <div className={style.rowColumnOne}>
              <DatePicker
                id="birthday"
                label="Date of Birth"
                className={style.inputField} 
                register={register}
                //value={parseDateString(getValues("birthday"))}
                value={parseDateString(contactBirthday || "") || new Date()}
                onChange={(value) => handleCalendarChange("birthday", value)}
                errors={errors}
                maxDate={new Date()}
                minDate={new Date(new Date().setFullYear(new Date().getFullYear() -100))}
                placeholder="YYYY-MM-DD"
              />
            </div>
          </div>
        </div>

        <div className={style.formSection}>
          <div className={style.title}>
            <h4><span>2</span> Updated Information and Documents</h4>
          </div>
          <div className={style.gridContainer}>
            <FileUpload
              id="passportUpload"
              label="Passport Bio Page (for all countries of Citizenship)"
              className={style.fileUpload}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "passportUpload")}
              errors={errors}
            />

            <FileUpload
              id="resumeUpload"
              label="Resume"
              className={style.fileUpload}
              subLabelClass={style.fileUploadSubtext}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "resumeUpload")}
              errors={errors}
            />

            <FileUpload
              id="canadianUpload"
              label="Canadian Status Document (if applicable)"
              className={style.fileUpload}
              subLabelClass={style.fileUploadSubtext}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "canadianUpload")}
              errors={errors}
            />

            <FileUpload
              id="jobDescUpload"
              label="Job Description (Including Job Title and Location)"
              className={style.fileUpload}
              subLabelClass={style.fileUploadSubtext}
              register={register}
              addDocuments={addDocuments}
              deleteDocument={deleteDocument}
              documentNames={getFieldDocumentNames(documentNames, "jobDescUpload")}
              errors={errors}
            />
          </div>
          <div className={style.gridContainerFull}>
            <MultilineInput
              id="textRequestDetails"
              label={""}
              placeholder="Have there been any changes in the employee's wages? If yes, please provide details."
              value={getValues("textRequestDetails")}
              onChange={(value) => handleFormValueChange("textRequestDetails", value)}
              rows={6}
              isReview={false}
              isComments={false}
              register={register}
              errors={errors}
              validationSchema={requestDetailsValidationSchema}
            />
          </div>
        </div>


        <div className={style.rowColumnTwo}>
          <div className={style.submitSection}>
            <Button 
              onClick={async () => {
                handleSubmit(onSubmit)();
              }} 
              id={'applicationSubmit'} 
              className={style.submitButton} 
              type="button" 
              style='gradient'
            >
              Submit
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};